/**
 * Browser Fullscreen API helpers, including the vendor-prefixed forms still
 * needed by Safari.
 */

interface WebkitFullscreenDocument extends Document {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
}

interface WebkitFullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => void;
}

export function isIOS(): boolean {
  // Old iOS
  if (/iPad|iPhone|iPod/.test(navigator.platform)) {
    return true;
  }

  // Chrome on iOS
  if (navigator.userAgent.match("CriOS")) {
    return true;
  }

  // New iOS
  if (
    navigator.maxTouchPoints &&
    navigator.maxTouchPoints > 2 &&
    /MacIntel/.test(navigator.platform)
  ) {
    return true;
  }

  return false;
}

/**
 * Whether this browser can put an element into true fullscreen.
 *
 * iOS is excluded: Safari on iPhone only supports fullscreen for video
 * elements, not arbitrary elements.
 */
export function fullscreenSupported(): boolean {
  if (isIOS()) {
    return false;
  }
  const doc = document as WebkitFullscreenDocument;
  return Boolean(doc.fullscreenEnabled || doc.webkitFullscreenEnabled);
}

/**
 * The element currently in true fullscreen, if any.
 *
 * Callers deciding whether they may treat the viewport as the full screen
 * should consult this at the moment of use rather than caching it: the user
 * can leave fullscreen at any time with Esc or F11, and browsers do not
 * always deliver that Esc keypress to the page.
 */
export function fullscreenElement(): Element | null {
  const doc = document as WebkitFullscreenDocument;
  return doc.fullscreenElement || doc.webkitFullscreenElement || null;
}

/**
 * Request true fullscreen for an element. Resolves to whether it was granted.
 *
 * Requires transient user activation, and is rejected outright in iframes that
 * lack `allow="fullscreen"`, so callers must treat `false` as an ordinary
 * outcome rather than an error.
 */
export async function requestFullscreen(element: HTMLElement): Promise<boolean> {
  if (!fullscreenSupported()) {
    return false;
  }

  try {
    if (element.requestFullscreen) {
      await element.requestFullscreen();
    } else {
      const webkit_element = element as WebkitFullscreenElement;
      if (!webkit_element.webkitRequestFullscreen) {
        return false;
      }
      webkit_element.webkitRequestFullscreen();
    }
  } catch {
    return false;
  }

  return Boolean(fullscreenElement());
}

/** Leave fullscreen, if this page is in it. */
export async function exitFullscreen(): Promise<void> {
  if (!fullscreenElement()) {
    return;
  }

  try {
    const doc = document as WebkitFullscreenDocument;
    if (document.exitFullscreen) {
      await document.exitFullscreen();
    } else if (doc.webkitExitFullscreen) {
      doc.webkitExitFullscreen();
    }
  } catch {
    // Leaving fullscreen is best-effort; the user can always exit manually.
  }
}
